import React, { useContext, useState } from 'react';

import {
  CircularProgress,
  IconButton,
  styled,
  Typography,
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import {
  getUnderwriterOverviewEndPoint,
  getUnderwritersEndPoint,
} from '../../common/api.endPoints';
import { HttpService } from '../../common/http.service';
import { AuthContext } from '../login/AuthWrapper';

import { Dropdown } from '@getethos/ethos-design-system-v2';
import { ArrowCircleRightOutlined } from '@mui/icons-material';
import { Roles } from '../../common/constants/roles';
import { FetchService } from '../../common/fetch.service';
import OverviewDetail, { OverviewType } from './OverviewDetail';

import styles from './Overview.module.scss';

export interface UnderwriterResponse {
  id: number;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  active: boolean;
  paused: boolean;
  userOrganization: string;
}

interface OverviewResponse {
  openCasesCount: number;
  actionableCasesCount: number;
  outsideSLACount: number;
  completedLast24HoursCount: number;
  completedLast30DaysCount: number;
}

export function OverviewPage() {
  const auth = useContext(AuthContext);
  // Not a permission related check just to determine the visibility of the user dropdown
  const isAdmin = auth.roles.some(role =>
    [Roles.ADMIN, Roles.WORKBENCH_ADMIN].includes(role),
  );

  const [selectedUnderwriterId, setSelectedUnderwriterId] = useState<
    number | null
  >(null);

  const [isMainView, setIsMainView] = useState(true);
  const [selectedType, setSelectedType] = useState<OverviewType | null>(null);

  const {
    data: overview,
    isLoading: isOverviewLoading,
    error,
  } = useQuery({
    queryKey: ['underwriterOverview', selectedUnderwriterId, auth.id],
    queryFn: async () => {
      if (!auth.id) {
        return null;
      }
      try {
        const idToUse = isAdmin ? selectedUnderwriterId : auth.id;
        if (!idToUse) {
          return null;
        }

        const endpoint = getUnderwriterOverviewEndPoint(idToUse);
        const response = await FetchService.getInstance().get(endpoint);
        return response as OverviewResponse;
      } catch (error: any) {
        if (error?.status === 403) {
          throw new Error('NO_ACCESS');
        }
        throw error;
      }
    },
    refetchOnWindowFocus: false,
  });

  const { data: underwriters, isLoading: isUnderwritersLoading } = useQuery<
    UnderwriterResponse[]
  >({
    queryKey: ['underwriters'],
    queryFn: async () => {
      const response = await HttpService.getInstance().get(
        getUnderwritersEndPoint(),
      );
      return response.parsedBody;
    },
    refetchOnWindowFocus: false,
  });

  if (!auth.id) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  const {
    openCasesCount,
    actionableCasesCount,
    outsideSLACount,
    completedLast24HoursCount,
    completedLast30DaysCount,
  } = overview || {};

  if (error !== null) {
    return (
      <div className={styles.loadingContainer}>
        <h1>You don't have access to see this page.</h1>
      </div>
    );
  }

  if (isAdmin && isUnderwritersLoading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  const handleViewDetails = (type: OverviewType) => {
    setSelectedType(type);
    setIsMainView(false);
  };

  if (!isMainView && selectedType) {
    // If the user is an admin, we need to get the underwriter from the dropdown
    // If the user is not an admin, we need to get the underwriter from the auth.id
    const selectedUnderwriterFromDropdown = underwriters?.find(
      uw => uw.id === selectedUnderwriterId,
    );
    const nonUWAdminUser = underwriters?.find(uw => uw.id === auth.id);
    const underwriter = isAdmin
      ? selectedUnderwriterFromDropdown
      : nonUWAdminUser;
    if (!underwriter) {
      return (
        <div className={styles.loadingContainer}>
          <h1>User not found</h1>
        </div>
      );
    }

    return (
      <OverviewDetail
        onBack={() => setIsMainView(true)}
        type={selectedType}
        underwriter={underwriter}
      />
    );
  }

  return (
    <>
      <div className={styles.overviewContainer}>
        {isAdmin && !isUnderwritersLoading && (
          <div className={styles.dropdownWrapper}>
            <Dropdown
              placeholder="Select an underwriter"
              className="" // empty classname to remove default styles
              value={
                selectedUnderwriterId
                  ? {
                      label:
                        underwriters?.find(
                          uw => uw.id === selectedUnderwriterId,
                        )?.displayName || '',
                      value: selectedUnderwriterId.toString(),
                    }
                  : null
              }
              options={
                underwriters?.map(underwriter => ({
                  label: underwriter.displayName,
                  value: underwriter.id.toString(),
                })) || []
              }
              onChange={(value: { label: string; value: string }) => {
                setSelectedUnderwriterId(Number(value.value));
              }}
            />
          </div>
        )}

        {isOverviewLoading ? (
          <div className={styles.loadingContainer}>
            <CircularProgress />
          </div>
        ) : isAdmin && !selectedUnderwriterId ? (
          <div>
            <p>Choose an underwriter to view their overview</p>
          </div>
        ) : (
          <>
            <StyledOverviewCard bgcolor="var(--secondary-ocean-5)">
              <Typography
                variant="bodyLMed"
                color="var(--secondary-ocean-100)"
                mb={2}
              >
                Queue Overview
              </Typography>
              <div className={styles.queueOverviewCardContainer}>
                {[
                  { label: 'Open cases', value: openCasesCount, type: 'open' },
                  {
                    label: 'Actionable cases',
                    value: actionableCasesCount,
                    type: 'actionable',
                  },
                  {
                    label: 'Outside SLA',
                    value: outsideSLACount,
                    type: 'outside-sla',
                  },
                ].map((item, index) => (
                  <div className={styles.card} key={index}>
                    <div className={styles.cardTextContainer}>
                      <div className={styles.cardHeader}>{item.label}</div>
                      <div className={styles.cardContent}>{item.value}</div>
                    </div>
                    <IconButton
                      onClick={() =>
                        handleViewDetails(item.type as OverviewType)
                      }
                    >
                      <ArrowCircleRightOutlined />
                    </IconButton>
                  </div>
                ))}
              </div>
            </StyledOverviewCard>
            <StyledOverviewCard bgcolor="var(--secondary-spearmint-5)">
              <Typography
                variant="bodyLMed"
                color="var(--secondary-spearmint-100)"
                mb={2}
              >
                Completed
              </Typography>
              <div className={styles.completedOverviewCardContainer}>
                {[
                  {
                    label: 'Completed last 24 hours',
                    value: completedLast24HoursCount,
                    type: 'completed-last-24-hours',
                  },
                  {
                    label: 'Completed last 30 days',
                    value: completedLast30DaysCount,
                    type: 'completed-last-30-days',
                  },
                ].map((item, index) => (
                  <div className={styles.card} key={index}>
                    <div className={styles.cardTextContainer}>
                      <div className={styles.cardHeader}>{item.label}</div>
                      <div className={styles.cardContent}>{item.value}</div>
                    </div>
                    <div>
                      <IconButton
                        onClick={() =>
                          handleViewDetails(item.type as OverviewType)
                        }
                      >
                        <ArrowCircleRightOutlined />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            </StyledOverviewCard>
          </>
        )}
      </div>
    </>
  );
}

const StyledOverviewCard = styled('div')(
  ({ bgcolor }: { bgcolor: string }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
    background: bgcolor,
    borderRadius: '10px',
  }),
);
